import { client } from "@/queries/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface Notification {
  id: number;
  play_id: number | null;
  title: string;
  message: string;
  timestamp: string;
  is_read: boolean;
}

export interface NotificationCreate {
  play_id?: number | null;
  title: string;
  message: string;
}

export function useGetNotifications() {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .GET("/api/notifications", { signal });
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to fetch notifications");
    },
  });
}

export function useCreateNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notification: NotificationCreate) => {
      const { data, error } = await client.get().POST("/api/notifications", {
        body: notification,
      });
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to create notification");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notification_id: number) => {
      const { data, error } = await client
        .get()
        .PATCH(`/api/notifications/${notification_id}/read` as any, {});
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to mark notification as read");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await client
        .get()
        .POST("/api/notifications/read-all");
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to mark all notifications as read");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useClearNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await client.get().DELETE("/api/notifications");
      if (data) return data;
      console.warn(error);
      throw new Error("Failed to clear notifications");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
